/**
 * Industry-specific icon configuration
 * 
 * Returns the appropriate icon component based on tenant industry.
 * Used for Assets/Equipment displays across the application.
 */
import { Truck } from 'lucide-react';

// Custom Excavator Icon for Earthmoving industry
const ExcavatorIcon = ({ className, ...props }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    {...props}
  >
    {/* Tracks/Base */}
    <rect x="2" y="18" width="12" height="3" rx="1.5" />
    {/* Body/Cab */}
    <rect x="3" y="14" width="8" height="4" rx="1" />
    {/* Cab window */}
    <rect x="8" y="15" width="2" height="2" rx="0.5" fill="currentColor" opacity="0.3" />
    {/* Boom arm base */}
    <path d="M11 14 L14 8" />
    {/* Boom arm */}
    <path d="M14 8 L20 6" />
    {/* Stick/Dipper arm */}
    <path d="M20 6 L22 10" />
    {/* Bucket */}
    <path d="M22 10 L20 13 L18 12 L20 9" />
    {/* Hydraulic cylinder hint */}
    <path d="M11 16 L13 11" strokeWidth="1" opacity="0.5" />
  </svg>
);

// Custom Mobile Crane Icon for Crane Hire industry
const MobileCraneIcon = ({ className, ...props }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    {...props}
  >
    {/* Wheels */}
    <circle cx="5" cy="20" r="2" />
    <circle cx="12" cy="20" r="2" />
    {/* Truck body/chassis */}
    <path d="M3 18 L14 18" />
    <rect x="2" y="14" width="12" height="4" rx="1" />
    {/* Cab */}
    <rect x="2" y="11" width="5" height="3" rx="0.5" />
    {/* Crane base/turntable */}
    <rect x="9" y="12" width="4" height="2" rx="0.5" />
    {/* Boom arm - angled up */}
    <path d="M11 12 L19 4" strokeWidth="2.5" />
    {/* Boom tip */}
    <path d="M19 4 L20 3" />
    {/* Hook cable */}
    <path d="M19 4 L19 8" strokeWidth="1" />
    {/* Hook */}
    <path d="M18 8 L20 8 L19 10 L18 8" fill="currentColor" />
    {/* Counterweight hint */}
    <rect x="12" y="13" width="2" height="1" rx="0.25" fill="currentColor" opacity="0.4" />
  </svg>
);

// Custom Agitator / Mixer Truck Icon for Concrete Pumping industry
const AgitatorTruckIcon = ({ className, ...props }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    {...props}
  >
    {/* Wheels */}
    <circle cx="6" cy="19" r="2" />
    <circle cx="17" cy="19" r="2" />
    {/* Chassis */}
    <path d="M2 19 H4 M8 19 H15 M19 19 H22" />
    {/* Cab */}
    <path d="M2 19 V12 H6 L8 14.5 V19" />
    {/* Cab window */}
    <rect x="3" y="13" width="2.5" height="2" rx="0.4" fill="currentColor" opacity="0.3" />
    {/* Mixer drum (tilted barrel) */}
    <path d="M8 14.5 L9 10 L20 8 L21 13 L11 16 Z" />
    {/* Drum mixing fins */}
    <path d="M12 10.5 L13 15" strokeWidth="1" opacity="0.6" />
    <path d="M16 9.5 L17 13.5" strokeWidth="1" opacity="0.6" />
  </svg>
);

// Industry icon mapping
const INDUSTRY_ICONS = {
  earthmoving: ExcavatorIcon,        // Excavator icon for earthmoving
  crane_hire: MobileCraneIcon,       // Mobile crane icon for crane hire
  haulage: Truck,                    // Truck icon for haulage
  concrete_pumping: AgitatorTruckIcon, // Agitator/mixer truck for concrete pumping
  default: Truck                     // Fallback to truck for unknown industries
};

/**
 * Get the appropriate icon component for the given industry
 * @param {string} industry - The tenant's industry type ('earthmoving', 'crane_hire', 'haulage', etc.)
 * @returns {React.ComponentType} - The icon component
 */
export const getAssetIcon = (industry) => {
  return INDUSTRY_ICONS[industry] || INDUSTRY_ICONS.default;
};

/**
 * Get all industry options with their icons
 * Useful for dropdowns and selection UIs
 */
export const INDUSTRY_OPTIONS = [
  { value: 'earthmoving', label: 'Earthmoving', icon: ExcavatorIcon },
  { value: 'crane_hire', label: 'Crane Hire', icon: MobileCraneIcon },
  { value: 'haulage', label: 'Haulage', icon: Truck },
  { value: 'concrete_pumping', label: 'Concrete Pumping', icon: AgitatorTruckIcon },
];

export { ExcavatorIcon, MobileCraneIcon, AgitatorTruckIcon };
export default getAssetIcon;
